import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getLivePrice } from './helpers';
import { archiveName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal, { mergeHistories } from './ItemDetailModal';
import { findSkinImage } from './skinIndex';
import { buildVault, getFolders, applyFolderPick, folderImage, UNFILED } from './investments';
import { useItemSets, planFileBySet, setById } from './collections';
import FolderPickModal from './FolderPickModal';
import FolderEditModal from './FolderEditModal';

// Investments 2 — the Vault in the classic theme. Same three levels as the Zone
// version (folder → position → unit) and the same rule book from
// investments.js; only the skin differs.
//
// Anything in here is absent from the Live tabs: an investment has one home.

const SORTS = [
  { key: 'value', label: 'Biggest position' },
  { key: 'gain',  label: 'Best gain' },
  { key: 'loss',  label: 'Worst gain' },
  { key: 'age',   label: 'Held longest' },
  { key: 'qty',   label: 'Most units' },
];

const SORTERS = {
  value: (a, b) => b.value - a.value,
  gain:  (a, b) => (b.plPct ?? -Infinity) - (a.plPct ?? -Infinity),
  loss:  (a, b) => (a.plPct ?? Infinity) - (b.plPct ?? Infinity),
  age:   (a, b) => b.days - a.days,
  qty:   (a, b) => b.qty - a.qty,
};

const money  = (n) => `$${Math.abs(Number(n) || 0).toFixed(2)}`;
const signed = (n) => `${(Number(n) || 0) >= 0 ? '+' : '−'}${money(n)}`;
const pctTxt = (n) => `${(Number(n) || 0) >= 0 ? '+' : '−'}${Math.abs(Number(n) || 0).toFixed(1)}%`;
const shortDate = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });

const floatText = (f) => (f == null ? 'n/a' : Number(f).toFixed(6));
const seedText  = (s) => (s == null ? 'n/a' : `#${s}`);

/** The float range across a position's units — what tells ten Factory New
 *  copies of one skin apart. One unit prints its own float instead. */
function floatSummary(units) {
  const floats = units.map(u => u.float).filter(f => f != null).map(Number);
  if (!floats.length) return null;
  if (units.length === 1 || floats.length === 1) return floatText(floats[0]);
  const lo = Math.min(...floats), hi = Math.max(...floats);
  return lo === hi ? floatText(lo) : `${floatText(lo)} – ${floatText(hi)}`;
}

/* ─── One unit inside an expanded position ────────────────── */
function UnitRow({ unit, folders, pcol, onSell, onEdit, onAssignFolder, onReturnToLive, onMove }) {
  const { row, realIndex } = unit;
  return (
    <div className="iv2-unit">
      <span className="dim">{shortDate(unit.bought)}</span>
      <span className="mono" title="Float value">{floatText(unit.float)}</span>
      <span className="mono" title="Paint seed / pattern index">{seedText(unit.paintSeed)}</span>
      <span>{money(unit.buy)}</span>
      <span style={{ color: unit.pl == null ? 'var(--text-dim)' : pcol(unit.pl) }}>
        {unit.pl == null ? 'n/a' : signed(unit.pl)}
        {unit.plPct != null && <em className="iv2-unit-pct">{pctTxt(unit.plPct)}</em>}
      </span>
      <span className="iv2-unit-acts">
        <button type="button" className="iv2-mini sell" title="Record this exact unit as sold or traded"
          onClick={() => onSell?.(row, realIndex)}>Sell</button>
        {/* Straight to any folder that exists. "New folder…" hands off to the
            picker, which is the only thing here that can make one. */}
        <select className="iv2-mini-select" value={row.investmentFolder || ''}
          aria-label="Move this unit"
          onChange={e => {
            const v = e.target.value;
            if (v === '__live__') onReturnToLive?.([realIndex]);
            else if (v === '__new__') onMove?.();
            else onAssignFolder?.([realIndex], v || null);
          }}>
          <option value="">Unfiled</option>
          {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          <option value="__new__">+ New folder…</option>
          <option value="__live__">← Back to Live</option>
        </select>
        <button type="button" className="iv2-mini" onClick={() => onEdit?.(row, realIndex)}>Edit</button>
      </span>
    </div>
  );
}

/* ─── One position ────────────────────────────────────────── */
function PositionRow({
  p, i, img, maxAbsPct, move, folders, expanded, profC, lossC, pcol,
  onToggle, onDetail, onSell, onEdit, onAssignFolder, onReturnToLive, onMove,
}) {
  const barW   = p.plPct != null ? Math.max(4, Math.abs(p.plPct) / maxAbsPct * 100) : 0;
  const spread = floatSummary(p.units);
  const seeds  = p.units.map(u => u.paintSeed).filter(s => s != null);

  return (
    <div className="iv2-pos-wrap">
      <motion.div className="iv2-pos"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 14) * 0.035 }}>

        {/* Clicking the count opens the list of actual items behind it. */}
        <button type="button" className="iv2-qty as-btn" aria-expanded={expanded}
          title={expanded ? 'Hide the individual items' : 'Show each item, with its float and pattern'}
          onClick={onToggle}>
          <span className="n">{p.qty}</span>
          <span className="x">unit{p.qty !== 1 ? 's' : ''}</span>
        </button>

        {img
          ? <img src={img} alt="" />
          : <div style={{ width: 58, height: 38, background: 'rgba(255,255,255,0.04)', borderRadius: 6, flexShrink: 0 }} />}

        <div className="iv2-main">
          <div className="iv2-name" title={p.skin}>{p.skin}</div>
          <div className="iv2-meta">
            <span>{p.cond || 'No condition'}</span>
            <span>held {p.days}d</span>
            <span>avg {money(p.avg)}/unit</span>
            {spread && <span className="mono" title="Float value">float {spread}</span>}
            {seeds.length === 1 && p.qty === 1 && (
              <span className="mono" title="Paint seed / pattern index">pattern #{seeds[0]}</span>
            )}
            {move && (
              <span title={readingNote(move)} style={{ color: move.change >= 0 ? profC : lossC }}>
                market {pctTxt(move.pct)} since (was {money(move.then)})
              </span>
            )}
          </div>
          {p.plPct != null && (
            <div className="iv2-gainbar">
              <motion.div className="fill"
                style={{ background: p.plPct >= 0
                  ? 'linear-gradient(90deg, rgba(34,197,94,0.5), var(--green-dim))'
                  : 'linear-gradient(90deg, rgba(248,113,113,0.5), var(--red))' }}
                initial={{ width: 0 }}
                animate={{ width: `${barW}%` }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 + Math.min(i, 14) * 0.04 }}
              />
            </div>
          )}
        </div>

        <div className="iv2-col">
          <div className="k">Cost</div>
          <div className="v">{money(p.cost)}</div>
        </div>
        <div className="iv2-col">
          <div className="k">Value</div>
          <div className="v">{p.price != null ? money(p.value) : 'n/a'}</div>
        </div>
        <div className="iv2-col" style={{ minWidth: 100 }}>
          <div className="k">P/L</div>
          <div className="v" style={{ color: p.pl == null ? 'var(--text-dim)' : pcol(p.pl) }}>
            {p.pl == null ? 'n/a' : signed(p.pl)}
            {p.plPct != null && (
              <span style={{ fontSize: '0.89rem', display: 'block', opacity: 0.75 }}>{pctTxt(p.plPct)}</span>
            )}
          </div>
        </div>

        <div className="iv2-pos-acts">
          <button type="button" className="iv2-mini" onClick={onDetail}>Details</button>
          {/* Moves every unit in the position at once. A position never
              straddles two folders, so there is one answer for the whole
              group and no need to expand it first. */}
          <button type="button" className="iv2-mini"
            title={`Move all ${p.qty} unit${p.qty !== 1 ? 's' : ''} to another folder`}
            onClick={() => onMove?.(p)}>Move</button>
          {p.qty === 1 && (
            <button type="button" className="iv2-mini sell"
              onClick={() => onSell?.(p.units[0].row, p.units[0].realIndex)}>Sell</button>
          )}
        </div>
      </motion.div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div className="iv2-units"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}>
            <div className="iv2-unit head">
              <span>Bought</span><span>Float</span><span>Pattern</span>
              <span>Cost</span><span>P/L now</span><span />
            </div>
            {p.units.map(u => (
              <UnitRow key={u.row.id || u.realIndex} unit={u} folders={folders} pcol={pcol}
                onSell={onSell} onEdit={onEdit}
                onAssignFolder={onAssignFolder} onReturnToLive={onReturnToLive}
                onMove={() => onMove?.(p, u)} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function InvestmentsTab2({
  rows, settings, skinsData = [], livePrices, csfloatPrices,
  onSell, onEdit, onCreateFolder, onUpdateFolder, onDeleteFolder,
  onAssignFolder, onReturnToLive,
}) {
  const [query, setQuery]   = useState('');
  const [sort, setSort]     = useState('value');
  const [detail, setDetail] = useState(null);
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const [openPositions, setOpenPositions] = useState(() => new Set());
  // The folder editor, wrapped in an object so "closed" and "open on a new
  // folder" stay distinct: null is closed, { folder: null } is create.
  const [editing, setEditing] = useState(null);
  // The units the folder picker is open for: { indexes, from, label }.
  const [moving, setMoving] = useState(null);

  const folders = useMemo(() => getFolders(settings), [settings]);
  const itemSets = useItemSets();
  const vault = useMemo(
    () => buildVault({ rows, livePrices, settings, getPrice: getLivePrice }),
    [rows, livePrices, settings],
  );

  // What the market did since each position was opened — drift you did not earn,
  // as opposed to the gain bar, which prices your entry.
  const archivePoints = useMemo(() => vault.positions.map(p => ({
    key: p.key, name: archiveName(p.skin, p.cond), date: dayOf(new Date(p.oldest)),
  })), [vault.positions]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const by = SORTERS[sort];
    return vault.groups
      .map(g => ({
        ...g,
        positions: (q ? g.positions.filter(p => p.skin.toLowerCase().includes(q)) : [...g.positions]).sort(by),
      }))
      .filter(g => g.positions.length > 0 || (!q && !g.unfiled));
  }, [vault.groups, query, sort]);

  const shown = useMemo(() => groups.reduce((s, g) => s + g.positions.length, 0), [groups]);

  const maxAbsPct = useMemo(
    () => Math.max(1, ...vault.positions.map(p => Math.abs(p.plPct ?? 0))),
    [vault.positions],
  );

  // Vault-wide market drift, weighted by units held, plus what your entry price
  // was worth against the market on the day — the two halves of the same P/L.
  const drift = useMemo(() => vault.positions.reduce((acc, p) => {
    const mv = archive.get(p.key);
    if (!mv) return acc;
    return {
      change: acc.change + mv.change * p.qty,
      base:   acc.base + mv.then * p.qty,
      n:      acc.n + 1,
      cost:   acc.cost + p.cost,
    };
  }, { change: 0, base: 0, n: 0, cost: 0 }), [vault.positions, archive]);
  const driftPct  = drift.base > 0 ? (drift.change / drift.base) * 100 : null;
  const entryEdge = drift.n ? drift.base - drift.cost : null;

  const toggle = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleFolder   = toggle(setOpenFolders);
  const togglePosition = toggle(setOpenPositions);

  // Creating and editing are the same modal, so a folder can be named, coloured
  // and given its picture in one pass instead of being made first and dressed
  // afterwards.
  const commitEdit = ({ name, color, image }) => {
    if (editing?.folder) onUpdateFolder?.(editing.folder.id, { name, color, image });
    else onCreateFolder?.(name, { color, image });
  };

  // One position, or one unit inside it when the unit's own select asked for a
  // folder that does not exist yet.
  const openMove = (p, unit = null) => {
    const units = unit ? [unit] : p.units;
    setMoving({
      indexes: units.map(u => u.realIndex),
      names: units.map(u => u.row.skin?.value).filter(Boolean),
      from: p.folderId,
      label: unit
        ? `${p.skin}${p.cond ? ` (${p.cond})` : ''}`
        : `${p.qty} × ${p.skin}${p.cond ? ` (${p.cond})` : ''}`,
    });
  };

  const commitMove = (pick) => {
    onAssignFolder?.(moving.indexes, applyFolderPick(pick, folders, onCreateFolder));
  };

  /**
   * File the whole unfiled pile by the collection, case or capsule each item
   * came from.
   *
   * Only the unfiled pile: anything already in a folder was put there on
   * purpose, and a bulk action that overrode that would be a worse trade than
   * leaving it alone. planFileBySet works out what can be placed and what the
   * user is told about it.
   */
  const fileBySet = () => {
    const plan = planFileBySet(itemSets, vault.positions.filter(p => p.folderId === UNFILED));
    if (!plan.units) { window.alert(plan.message); return; }
    if (!window.confirm(plan.message)) return;
    for (const g of plan.groups) {
      onAssignFolder?.(g.indexes, applyFolderPick({ set: g.set }, folders, onCreateFolder));
    }
  };

  const removeFolder = (g) => {
    const msg = g.units > 0
      ? `Delete "${g.name}"? Its ${g.units} unit${g.units !== 1 ? 's' : ''} stay in the vault and become unfiled.`
      : `Delete "${g.name}"?`;
    if (window.confirm(msg)) onDeleteFolder?.(g.id);
  };

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';
  const pcol  = (v) => (v >= 0 ? profC : lossC);

  const totals = vault.totals;

  if (!vault.positions.length && !folders.length) {
    return (
      <div className="iv2-root">
        <div className="lv2-empty">
          The vault is empty — move items here from Live and they leave that tab.
          An investment has one home, not two.
        </div>
      </div>
    );
  }

  return (
    <div className="iv2-root">

      {/* ── Hero totals ────────────────────────────── */}
      <div className="iv2-hero">
        <div className="iv2-hero-cell">
          <div className="k">Positions</div>
          <div className="v">
            {totals.count}
            <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}> · {totals.units} units</span>
          </div>
          <div className="s">{totals.oldestDays > 0 ? `oldest hold: ${totals.oldestDays}d` : 'all opened today'}</div>
        </div>
        <div className="iv2-hero-cell">
          <div className="k">Capital in vault</div>
          <div className="v">{money(totals.cost)}</div>
          <div className="s">across {totals.units} unit{totals.units !== 1 ? 's' : ''}</div>
        </div>
        <div className="iv2-hero-cell">
          <div className="k">Current value</div>
          <div className="v" style={{ color: 'var(--indigo)' }}>{money(totals.value)}</div>
          <div className="s">
            {totals.priced < totals.count
              ? `${totals.priced} of ${totals.count} positions priced`
              : 'at live market prices'}
          </div>
        </div>
        {drift.n > 0 && (
          <div className="iv2-hero-cell">
            <div className="k">Market since you bought</div>
            <div className="v" style={{ color: pcol(drift.change) }}>
              {signed(drift.change)}
              {driftPct != null && (
                <span style={{ fontSize: '0.92rem', fontWeight: 700 }}> ({pctTxt(driftPct)})</span>
              )}
            </div>
            <div className="s">
              {entryEdge != null && (
                <>you bought {entryEdge >= 0 ? 'under' : 'over'} market by {money(entryEdge)}{' · '}</>
              )}
              {drift.n} of {totals.count} priced from history
            </div>
          </div>
        )}
        <div className="iv2-hero-cell">
          <div className="k">Vault P/L</div>
          <div className="v" style={{ color: pcol(totals.pl) }}>
            {signed(totals.pl)}
            {totals.cost > 0 && (
              <span style={{ fontSize: '0.92rem', fontWeight: 700 }}> ({pctTxt(totals.pl / totals.cost * 100)})</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 14 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search the vault…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <button type="button" className="iv2-mini" onClick={() => setEditing({ folder: null })}>+ New folder</button>
        {itemSets.sets.length > 0 && (
          <button type="button" className="iv2-mini" onClick={fileBySet}
            title="Sort every unfiled unit into a folder named after the collection, case or capsule it came from">
            File by collection
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {shown} of {totals.count} positions
        </span>
        <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort positions">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Folders ────────────────────────────────── */}
      {groups.length === 0 ? (
        <div className="lv2-empty">Nothing matches the search.</div>
      ) : groups.map(g => {
        const open = openFolders.has(g.id);
        // A folder wears a picture instead of the colour chip: the one the user
        // picked in the editor, or the icon of the set it stands for. Either
        // says which folder at a glance, which the chip never could.
        const folderSet = setById(itemSets, g.setId);
        const img = folderImage(g, folderSet);
        return (
          <div key={g.id} className={`iv2-fold${open ? ' open' : ''}`}>
            <div className="iv2-fold-head">
              <button type="button" className="iv2-fold-toggle" aria-expanded={open}
                onClick={() => toggleFolder(g.id)}>
                <span className="chev">{open ? '▾' : '▸'}</span>
                {img
                  ? <img className="iv2-fold-img" src={img} alt="" loading="lazy" />
                  : <span className="swatch" style={{ background: g.color || 'var(--text-dim)' }} />}
                <span className="iv2-fold-name">{g.name}</span>
                <span className="iv2-fold-count">
                  {g.positions.length} position{g.positions.length !== 1 ? 's' : ''} · {g.units} unit{g.units !== 1 ? 's' : ''}
                </span>
              </button>

              <div className="iv2-fold-nums">
                <div className="iv2-col">
                  <div className="k">Cost</div>
                  <div className="v">{money(g.cost)}</div>
                </div>
                <div className="iv2-col">
                  <div className="k">Value</div>
                  <div className="v">{g.priced ? money(g.value) : 'n/a'}</div>
                </div>
                <div className="iv2-col" style={{ minWidth: 100 }}>
                  <div className="k">P/L</div>
                  <div className="v" style={{ color: g.priced ? pcol(g.pl) : 'var(--text-dim)' }}>
                    {g.priced ? signed(g.pl) : 'n/a'}
                    {g.priced > 0 && g.plPct != null && (
                      <span style={{ fontSize: '0.89rem', display: 'block', opacity: 0.75 }}>{pctTxt(g.plPct)}</span>
                    )}
                  </div>
                </div>
                {/* The unfiled pile is not a folder anyone made, so there is
                    nothing to edit and nothing to delete. */}
                {!g.unfiled && (
                  <div className="iv2-fold-acts">
                    <button type="button" className="iv2-mini"
                      title="Rename it, recolour it, or give it a picture"
                      onClick={() => setEditing({ folder: folders.find(f => f.id === g.id) || null })}>Edit</button>
                    <button type="button" className="iv2-mini danger"
                      onClick={() => removeFolder(g)}>Delete</button>
                  </div>
                )}
              </div>
            </div>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div className="iv2-fold-body"
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>
                  {g.positions.length === 0 ? (
                    <div className="iv2-fold-blank">
                      Empty. Open a position elsewhere in the vault and move a unit in here.
                    </div>
                  ) : g.positions.map((p, i) => (
                    <PositionRow key={p.key} p={p} i={i}
                      img={findSkinImage(skinsData, p.skin, p.cond)}
                      maxAbsPct={maxAbsPct}
                      move={archive.get(p.key)}
                      folders={folders}
                      expanded={openPositions.has(p.key)}
                      profC={profC} lossC={lossC} pcol={pcol}
                      onToggle={() => togglePosition(p.key)}
                      onDetail={() => setDetail({ ...p, img: findSkinImage(skinsData, p.skin, p.cond) })}
                      onSell={onSell} onEdit={onEdit}
                      onAssignFolder={onAssignFolder} onReturnToLive={onReturnToLive}
                      onMove={openMove} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {editing && (
        <FolderEditModal
          folder={editing.folder}
          folders={folders}
          skinsData={skinsData}
          variant="classic"
          onSave={commitEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {moving && (
        <FolderPickModal
          folders={folders}
          initialId={moving.from}
          names={moving.names}
          variant="classic"
          title="Move to another folder"
          subtitle={moving.label}
          confirmLabel={`Move ${moving.indexes.length} unit${moving.indexes.length !== 1 ? 's' : ''}`}
          note="The items stay in the vault either way."
          onConfirm={commitMove}
          onClose={() => setMoving(null)}
        />
      )}

      {detail && (
        <ItemDetailModal
          skinsData={skinsData}
          variant="classic"
          kind="position"
          name={detail.skin}
          condition={detail.cond}
          image={detail.img}
          history={mergeHistories(detail.units.map(u => u.row.priceHistory || []))}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          qty={detail.qty}
          cost={detail.cost}
          avg={detail.avg}
          days={detail.days}
          units={detail.units.map(u => u.row)}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
